import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { CheckSquare, ClipboardCheck, DollarSign, FormInput, MoreHorizontal, Sun } from "lucide-react";

const GeneralTab = ({
   currentTheme,
   handleThemeToggle,
   typewriterEnabled,
   handleTypewriterToggle,
   brightness,
   handleBrightnessChange,
   autoCheckEnabled,
   handleAutoCheckToggle,
   compactMenuEnabled,
   handleCompactMenuToggle,
   verificationEnabled,
   handleVerificationToggle,
   pltpVerificationEnabled,
   handlePLTPVerificationToggle,
   csawValidationEnabled,
   handleCSAWValidationToggle,
   covValidationEnabled,
   handleCOVValidationToggle,
   ptprValidationEnabled,
   handlePTPRValidationToggle,
   mockFinancialDataEnabled,
   handleMockFinancialDataToggle
}) => {
   return (
      <Card>
         <CardHeader>
            <CardTitle>General Settings</CardTitle>
            <CardDescription>Manage appearance and behavior settings</CardDescription>
         </CardHeader>
         <CardContent className="space-y-4">
            {/* Theme Toggle */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <Label>Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                     Toggle between light and dark theme
                  </p>
               </div>
               <Switch
                  checked={currentTheme === 'dark'}
                  onCheckedChange={handleThemeToggle}
               />
            </div>

            {/* Typewriter Effect */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <Label>Typewriter Effect</Label>
                  <p className="text-sm text-muted-foreground">
                     Enable typing animation for descriptions
                  </p>
               </div>
               <Switch
                  checked={typewriterEnabled}
                  onCheckedChange={handleTypewriterToggle}
               />
            </div>

            {/* Auto-check Authenticity Review */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                     <Label>Auto-check Authenticity Review</Label>
                     <CheckSquare className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                     Automatically check all boxes in authenticity review
                  </p>
               </div>
               <Switch
                  checked={autoCheckEnabled}
                  onCheckedChange={handleAutoCheckToggle}
               />
            </div>

            {/* Auto check release confirmation */}
            <div className="space-y-4">
               {/* <h3 className="text-lg font-semibold">Verification Settings</h3> */}
               <div className="space-y-4">
                  <div className="flex items-center justify-between">
                     <div className="space-y-0.5">
                        <Label>Auto-check Verification</Label>
                        <p className="text-[0.8rem] text-muted-foreground">
                           Automatically check all verification boxes in authenticity review and release confirmation
                        </p>
                     </div>
                     <Switch
                        checked={autoCheckEnabled}
                        onCheckedChange={handleAutoCheckToggle}
                     />
                  </div>
               </div>
            </div>

            {/* Mock Financial Data Toggle */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                     <Label>Mock Financial Data</Label>
                     <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                     Use mock data for financial analytics instead of real data
                  </p>
               </div>
               <Switch
                  checked={mockFinancialDataEnabled}
                  onCheckedChange={handleMockFinancialDataToggle}
               />
            </div>

            {/* Expanded Menu Toggle */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                     <Label>Expanded Menu</Label>
                     <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                     Use expanded action buttons instead of 3-dot menu
                  </p>
               </div>
               <Switch
                  checked={compactMenuEnabled}
                  onCheckedChange={handleCompactMenuToggle}
               />
            </div>

            {/* Inventory Verification Toggle */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                     <Label>Inventory Verification</Label>
                     <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                     Require inventory verification for cutting/earth balling requests
                  </p>
               </div>
               <Switch
                  checked={verificationEnabled}
                  onCheckedChange={handleVerificationToggle}
               />
            </div>

            {/* PLTP Verification Toggle */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                     <Label>PLTP Verification</Label>
                     <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                     Require PLTP permit verification for COV applications
                  </p>
               </div>
               <Switch
                  checked={pltpVerificationEnabled}
                  onCheckedChange={handlePLTPVerificationToggle}
               />
            </div>

            {/* CSAW Form Validation Toggle */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                     <Label>CSAW Form Validation</Label>
                     <FormInput className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                     Enable form validation for chainsaw registration
                  </p>
               </div>
               <Switch
                  checked={csawValidationEnabled}
                  onCheckedChange={handleCSAWValidationToggle}
               />
            </div>

            {/* COV Form Validation Toggle */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                     <Label>COV Form Validation</Label>
                     <FormInput className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                     Enable form validation for certificate of verification
                  </p>
               </div>
               <Switch
                  checked={covValidationEnabled}
                  onCheckedChange={handleCOVValidationToggle}
               />
            </div>

            {/* PTPR Form Validation Toggle */}
            <div className="flex items-center justify-between">
               <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                     <Label>PTPR Form Validation</Label>
                     <FormInput className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                     Enable form validation for private tree plantation registration
                  </p>
               </div>
               <Switch
                  checked={ptprValidationEnabled}
                  onCheckedChange={handlePTPRValidationToggle}
               />
            </div>

            {/* Brightness Control */}
            <div className="space-y-2">
               <Label>Screen Brightness</Label>
               <div className="flex items-center gap-4">
                  <Sun className="h-4 w-4" />
                  <Slider
                     value={[brightness]}
                     max={100}
                     min={30}
                     step={1}
                     className="flex-1"
                     onValueChange={handleBrightnessChange}
                  />
                  <span className="w-12 text-sm">{brightness}%</span>
               </div>
            </div>

         </CardContent>
      </Card>
   );
};

export default GeneralTab;